import type { JsonSchemaType, Object as PortalObject } from '@gmn-portal/backend';
import type { TFunction } from 'i18next';
import { DateTime } from 'luxon';
import { useObject, useObjectCategory, useObjectVersions } from '@/core/api';
import type { BackendActivityItem } from '@/core/api/types';
import { useIntl } from '@/core/i18n/use-intl';
import { ResolvedUser } from '@/shared/components/activity';
import { ActivityTimeline } from '@/shared/components/activity/activity-timeline';

import { ResolvedFieldValue } from './resolved-field-value';

function getFieldNameForObject(
  field: string,
  objectSchema?: JsonSchemaType,
  categorySchema?: JsonSchemaType,
  t?: TFunction<'compat'>
): string | null {
  // Handle regular fields (not metadata)
  if (!field.startsWith('dynamicMetadata.') && !field.startsWith('categoryMetadata.')) {
    return t
      ? (t(`activity.object.fields.${field}` as unknown as never) as unknown as string)
      : `activity.object.fields.${field}`;
  }

  // Handle metadata fields (both categoryMetadata and dynamicMetadata)
  const [prefix, key] = field.split('.');

  // For categoryMetadata, look in categorySchema; for dynamicMetadata, look in both
  let schemaSource: JsonSchemaType | undefined;
  if (prefix === 'categoryMetadata') {
    schemaSource = categorySchema?.properties?.[key] ? categorySchema : undefined;
  } else {
    // dynamicMetadata - check object schema first, then category schema
    schemaSource =
      (objectSchema?.properties?.[key] && objectSchema) ||
      (categorySchema?.properties?.[key] && categorySchema);
  }

  if (!schemaSource?.properties?.[key]) {
    return null; // Field has been deleted from schema
  }

  const description = schemaSource.properties[key]?.description;
  if (description) {
    return description;
  }

  return t
    ? (t(`activity.object.fields.${field}` as unknown as never) as unknown as string)
    : `activity.object.fields.${field}`;
}

export interface ObjectActivityProps {
  objectId?: string;
}

export function ObjectActivity({ objectId }: ObjectActivityProps) {
  const { t, locale } = useIntl();
  const {
    data: activityData,
    isLoading: isLoadingActivities,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useObjectVersions(objectId!, {
    disabled: !objectId,
    limit: 15,
  });

  const { data: object } = useObject(objectId!);
  const categoryRelation = object?.category as PortalObject['category'] | string | undefined;
  const categoryId =
    typeof categoryRelation === 'object'
      ? categoryRelation?.id
      : (categoryRelation as string | undefined);
  const { data: fetchedCategory } = useObjectCategory(categoryId || '', {
    disabled: !categoryId || typeof categoryRelation === 'object',
  });
  const categorySchema =
    (typeof categoryRelation === 'object'
      ? (categoryRelation?.metadataSchema as JsonSchemaType | undefined)
      : (fetchedCategory?.metadataSchema as JsonSchemaType | undefined)) ?? undefined;

  if (isLoadingActivities) {
    return <div className="text-center text-muted-foreground">{t('activity.loading')}</div>;
  }

  if (!activityData?.pages[0].docs.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  // Flatten all pages of activities
  const allActivities = activityData.pages.flatMap((page) => page.docs);

  // Filter activities to show relevant ones
  const objectSchema = object?.dynamicMetadataSchema as JsonSchemaType | undefined;
  const filteredActivities = allActivities.filter((activity: BackendActivityItem) => {
    if (activity.type === 'created') return !hasNextPage;
    return activity.changes?.some((change) => {
      const fieldName = getFieldNameForObject(change.field, objectSchema, categorySchema, t);
      return fieldName !== null;
    });
  });

  if (!filteredActivities.length) {
    return <div className="text-center text-muted-foreground">{t('activity.noActivity')}</div>;
  }

  return (
    <div className="space-y-4">
      <ActivityTimeline
        activities={filteredActivities.map((activity) => ({
          id: activity.id,
          type: activity.type as 'created' | 'updated',
          timestamp: activity.timestamp,
          user: activity.user,
          changes: activity.changes,
        }))}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
      >
        {(activity) =>
          activity.type === 'created' ? (
            <div className="flex items-center gap-1">
              <ResolvedUser userId={activity.user!} />
              <span className="text-muted-foreground">{t('activity.object.created')}</span>
              <span className="text-muted-foreground">
                · {DateTime.fromISO(activity.timestamp).toRelative({ locale })}
              </span>
            </div>
          ) : (
            activity.changes?.map((change, index) => {
              // Skip if field has been deleted
              const fieldName = getFieldNameForObject(
                change.field,
                objectSchema,
                categorySchema,
                t
              );
              if (fieldName === null) return null;

              return (
                <div key={index} className="flex items-center gap-1">
                  <ResolvedUser userId={activity.user!} />
                  <span className="text-muted-foreground">{t('activity.changed')}</span>
                  {change.field.startsWith('dynamicMetadata.') ||
                  change.field.startsWith('categoryMetadata.') ? (
                    <span>
                      <span className="text-muted-foreground">{t('activity.metadataField')}</span>
                      <span className="font-medium"> {fieldName}</span>
                    </span>
                  ) : (
                    <span className="font-medium">{fieldName}</span>
                  )}
                  <span className="text-muted-foreground">{t('activity.from')}</span>
                  <span className="font-medium">
                    <ResolvedFieldValue
                      field={change.field}
                      value={change.from}
                      objectSchema={objectSchema}
                      categorySchema={categorySchema}
                      category={object?.category}
                      assignedCustomer={object?.assignedCustomer}
                    />
                  </span>
                  <span className="text-muted-foreground">{t('activity.to')}</span>
                  <span className="font-medium">
                    <ResolvedFieldValue
                      field={change.field}
                      value={change.to}
                      objectSchema={objectSchema}
                      categorySchema={categorySchema}
                      category={object?.category}
                      assignedCustomer={object?.assignedCustomer}
                    />
                  </span>
                  <span className="text-muted-foreground">
                    · {DateTime.fromISO(activity.timestamp).toRelative({ locale })}
                  </span>
                </div>
              );
            })
          )
        }
      </ActivityTimeline>
    </div>
  );
}
